import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ensureStudiesEnabled } from "@/features/studies/stage";
import { StudiesBoard } from "@/features/studies/StudiesBoard";
import { StudiesPageLayout } from "@/features/studies/StudiesPageLayout";
import { NewStudyAction } from "@/features/studies/StudiesToolbar";
import {
  dropRetiredListParams,
  parseStudiesListSearch,
  rememberStudiesListSearch,
  workspaceListSearch,
  type StudiesListSearch,
} from "@/features/studies/studiesList";

/** The current workspace's studies — for everyone, platform admins included (their
 *  all-organization board is `/admin/studies`). */
function StudiesIndex() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  // "Studies" from inside a study comes back to exactly this list.
  useEffect(() => rememberStudiesListSearch(search), [search]);

  return (
    <StudiesPageLayout description="Design research studies to understand markets and companies">
      <StudiesBoard
        action={<NewStudyAction to="/studies/new" />}
        search={search}
        onSearchChange={(next: StudiesListSearch) =>
          void navigate({ to: "/studies", search: next, replace: true })
        }
      />
    </StudiesPageLayout>
  );
}

export const Route = createFileRoute("/_auth/_agents/studies/")({
  // The search filter is URL state; the workspace list has no organization filter.
  validateSearch: (search: Record<string, unknown>): StudiesListSearch =>
    workspaceListSearch(parseStudiesListSearch(search)),
  beforeLoad: ({ context, preload }) => ensureStudiesEnabled(context.queryClient, preload),
  // Old bookmarks' retired `status` param is stripped from the URL (the columns group by status).
  search: { middlewares: [dropRetiredListParams] },
  component: StudiesIndex,
});
